"use client";

// "use client" hoja interactiva: copy localizada (useT) + reveals con IntersectionObserver — sin estado propio.

// 2026-10-29 simplificación (pedido del dueño: menos texto, misma belleza):
// una sola superficie por paso en vez de 2–3 cajas apiladas (la caja de fuentes
// se desbordaba a 768px y tapaba el número 02), número pequeño junto al título
// en vez del numeral gigante, y descripciones de una línea en las 14 locales.
// Contratos intactos: .how-card ×3, 01/02/03 tabular-nums, howStep*Title/Desc,
// sin CTA por card (la conversión vive en Hero/FinalCta).
import { c as _c } from "react/compiler-runtime";
import { FileDown, FileText, Link2, MessageSquare } from "lucide-react";
import { AccentTail, Reveal } from "./Reveal";
import { useT } from "@/lib/use-i18n";
import type { TranslationKey } from "@/lib/i18n/locales";
const STEPS: Array<{
  num: string;
  titleKey: TranslationKey;
  descKey: TranslationKey;
}> = [{
  num: "01",
  titleKey: "howStep1Title",
  descKey: "howStep1Desc"
}, {
  num: "02",
  titleKey: "howStep2Title",
  descKey: "howStep2Desc"
}, {
  num: "03",
  titleKey: "howStep3Title",
  descKey: "howStep3Desc"
}];
export function HowItWorks() {
  const $ = _c(18);
  const t = useT();
  let t0;
  if ($[0] !== t) {
    t0 = t("howTitle");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <h2 id="how-it-works-heading" className="text-section-heading text-expressive-emphasized tracking-[-0.02em] text-foreground text-[var(--step-3)] font-bold text-balance"><AccentTail text={t0} /></h2>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("howBody");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <p className="mt-4 mx-auto max-w-[54ch] text-[15px] sm:text-base leading-relaxed text-muted-foreground text-pretty">{t2}</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <Reveal className="mb-12 sm:mb-16 max-w-2xl mx-auto text-center">{t1}{t3}</Reveal>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = STEPS.map((step, idx) => <li key={step.titleKey} className="feature-card how-card lovable-card group flex flex-col justify-between gap-5 p-6 sm:p-7 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"><div className="relative h-36 w-full rounded-[var(--radius-panel)] border border-border/60 bg-muted/20 dark:bg-card/40 p-4 overflow-hidden flex items-center justify-center select-none shadow-xs transition-colors duration-300 group-hover:border-border/80">{idx === 0 && <div className="relative size-full flex items-center justify-center"><div className="hero-generated-art opacity-30 absolute -top-6 -left-6 h-[calc(100%+48px)] w-[120%] pointer-events-none" aria-hidden="true" /><div className="relative z-10 w-full max-w-[260px] rounded-[var(--radius-panel)] border border-border/80 bg-[var(--background-elevated)] p-3 shadow-[var(--shadow-panel)] dark:border-white/10"><div className="flex items-center gap-2 text-[13px] text-muted-foreground font-sans"><MessageSquare className="size-3.5 shrink-0 text-foreground/70" aria-hidden="true" /><span className="min-w-0 truncate text-foreground font-medium">{t("howPreviewPrompt")}</span><span className="inline-block h-3.5 w-0.5 shrink-0 animate-pulse motion-reduce:animate-none bg-[var(--accent)]" /></div></div></div>}{idx === 1 && <div className="relative z-10 w-full max-w-[260px] rounded-[var(--radius-panel)] border border-border/80 bg-[var(--background-elevated)] p-3 shadow-[var(--shadow-panel)] dark:border-white/10"><div className="flex items-center gap-2 text-xs font-semibold text-foreground"><FileText className="size-3.5 shrink-0 text-[var(--accent)]" aria-hidden="true" /><span className="leading-tight truncate">{t("howMock2Title")}</span></div><div className="mt-2.5 flex items-center gap-1.5" aria-hidden="true">{[1, 2, 3, 4, 5].map(_temp)}</div><p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Link2 className="size-3 shrink-0 text-[var(--accent)]" aria-hidden="true" /><span className="truncate">{t("howMock2Sources")}</span></p></div>}{idx === 2 && <div className="relative z-10 w-full max-w-[260px] rounded-[var(--radius-panel)] border border-border/80 bg-[var(--background-elevated)] p-3 shadow-[var(--shadow-panel)] dark:border-white/10"><div className="flex items-center gap-2 text-xs font-semibold text-foreground"><FileDown className="size-3.5 shrink-0 text-[var(--accent)]" aria-hidden="true" /><span className="leading-tight truncate">{t("howMock3Title")}</span></div><p className="mt-1.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground" title={t("howMock3File")}><FileText className="size-3 shrink-0 opacity-70" aria-hidden="true" /><span className="min-w-0 flex-1 truncate font-mono text-[11.5px]">{t("howMock3File")}</span></p><div className="mt-2 flex items-center justify-between border-t border-border/40 pt-1.5 text-[11px] font-medium text-muted-foreground"><span className="tabular-nums font-semibold text-foreground">{t("howMock3Meta")}</span><span className="inline-flex items-center gap-1 text-[var(--accent)] font-semibold">DOCX · PDF</span></div></div>}</div><div className="w-full"><div className="flex items-center gap-2"><span className="inline-flex items-center justify-center rounded-md border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums tracking-wider text-muted-foreground" aria-hidden="true">{step.num}</span></div><h3 className="mt-2 text-[15px] font-semibold tracking-tight text-foreground">{t(step.titleKey)}</h3><p className="mt-1.5 text-pretty text-[13.5px] leading-relaxed text-muted-foreground">{t(step.descKey)}</p></div></li>);
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <Reveal delay={100}><ol className="grid gap-4 list-none p-0 sm:gap-6 md:grid-cols-3">{t5}</ol></Reveal>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t6) {
    t7 = <section id="how-it-works" className="landing-section no-x-overflow py-20 sm:py-28 bg-background text-foreground transition-colors" aria-labelledby="how-it-works-heading"><div className="mx-auto max-w-container-wide gutter-fluid">{t4}{t6}</div></section>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp(n) {
  return <span key={n} className="flex size-5.5 items-center justify-center rounded-md border border-border/60 bg-foreground/5 text-[11px] font-semibold tabular-nums text-foreground transition-colors group-hover:bg-foreground/10">{n}</span>;
}